import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";

export default function PortalUsersTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
        rolesDisplay: Array.isArray(docItem.data().roles)
          ? docItem.data().roles.join(", ")
          : "",
      }));
      setRows(data);
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async () => {
    try {
      const newUser = {
        email: "new.user@example.com",
        displayName: "New User",
        roles: [],
        department: "",
        enabled: true,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || "admin",
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "admin",
      };

      await addDoc(collection(db, "users"), newUser);
      await loadUsers();
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await deleteDoc(doc(db, "users", id));
      await loadUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      const updatePayload = {
        email: newRow.email,
        displayName: newRow.displayName || "",
        roles: newRow.rolesDisplay
          ? newRow.rolesDisplay.split(",").map((r) => r.trim()).filter(Boolean)
          : [],
        department: newRow.department || "",
        enabled: Boolean(newRow.enabled),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "admin",
      };

      const docRef = doc(db, "users", newRow.id);
      await updateDoc(docRef, updatePayload);

      const updatedRow = {
        ...oldRow,
        ...newRow,
        roles: updatePayload.roles,
      };

      setRows((prev) =>
        prev.map((row) => (row.id === newRow.id ? updatedRow : row))
      );

      return updatedRow;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  };

  const columns = [
    { field: "id", headerName: "User ID", width: 200, editable: false },
    { field: "email", headerName: "Email", width: 220, editable: true },
    { field: "displayName", headerName: "Display Name", width: 180, editable: true },
    {
      field: "rolesDisplay",
      headerName: "Roles (comma-separated)",
      width: 220,
      editable: true,
    },
    { field: "department", headerName: "Department", width: 150, editable: true },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 100,
      type: "boolean",
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => handleDeleteUser(params.row.id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: 700 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Portal Users</Typography>
        <Button variant="contained" size="small" onClick={handleAddUser}>
          Add User
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        editMode="row"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error("Failed to save row update:", error);
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />
    </Box>
  );
}
